/**
 * Ryan O'Dowd
 * 2024-02-08
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
} from 'react';
import Assignments from './Assignments';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import ScreenWrapper from '@containers/ScreenWrapper';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const Assignment = () => {
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const user = hooks.useTypedSelector((state) => state.oakUser);

  const goal = hooks.useTypedSelector((state) => state.goals[locationState.goalId] || state.goalPreviews[locationState.goalId]);

  const assignment = goal.modules.flatMap((m) => m.assignments).find((a) => +a.id === +locationState.assignmentId); // @TODO: should this be memoized?
  const hideActions = locationState.hideActions;

  const progress = hooks.useTypedSelector((state) => state.goalProgress[goal.id]?.[user.id]?.[assignment.id]) || {};

  const assignmentStartTimestamp = useMemo(() => {
    return Date.now();
  }, []);

  let title = assignment.name;
  const passage: any = assignment.assignment_object.passage ? CPassage.createFromObject(assignment.assignment_object.passage) : {};

  if (assignment.assignment_type === 'reading') {
    title = passage.prettyPassageName();
  }

  const backIcon = <Header.BackIcon icon='close' />;

  const finishButtonJsx = (
    <View style={styles.finishButtonWrapper}>
      {/* @TODO: info about progress...info popover explaining why button is disabled, for example */}
      <Button
        mode='contained'
        theme={styles.colors.accent}
        isLarge={true}
        // @TODO: should be disabled with progress based on scroll distance in read type, and similar ui/ux for other types: disabled={TODO}
        // @TODO: disabled, and change text to "completed" or something when it's done
        onPress={() => {
          const numVersesReviewed = assignment.assignment_object.passage ? (assignment.assignment_object.passage.end_verse_number - assignment.assignment_object.passage.start_verse_number + 1) : 0;
          const millisecondsSpent = Date.now() - assignmentStartTimestamp;
          let totalPeanutsEarned = 10;
          if (assignment.assignment_type === 'reading') {
            // award 2 peanuts per verse (or 0.5 if already completed); cap at 1 verse per 2 seconds to avoid spamming
            const peanutsPerVerse = assignment.is_completed ? 0.5 : 2;
            totalPeanutsEarned = peanutsPerVerse * Math.min(numVersesReviewed, millisecondsSpent / 2000);
          } else {
            // lecture or video
            totalPeanutsEarned = assignment.is_completed ? 0 : 10;
          }

          utilities.saveLesson({
            userId: user.id,
            passageId: assignment.assignment_object.passage?.id,
            millisecondsSpent,
            peanutsEarned: Math.ceil(totalPeanutsEarned),
            difficulty: assignment.assignment_type,
            numberOfVersesReviewed: numVersesReviewed,
            numberOfQuestions: 0, // @TODO: questions reviewed...what if this is verse_memory? // @TODO: update
            minigameIds: [], // @TODO: minigames available...should this have values?
            goalId: goal.id,
            testPassedVerseNums: null,
            assignmentId: assignment.id,
            adHocPassage: null,
            streakRepairDate: null,
            passageOfTheDayId: null,
            numberOfSkippedQuestions: 0, // no questions to skip if it's not a verse_memory assignment
          });
          navigation.goBack();
        }}
      >
        {i18n('finishAssignment')}
      </Button>
    </View>
  );

  return (
    <ScreenWrapper>
      <Header
        title={title}
        leftItems={[backIcon]}
        isMultilineAllowed={true}
        // @TODO: rightItems depending on assignment_type (verse memroy should have settings cog, for example)
      />
      <FullScreenScrollView
        contentContainerStyle={styles.innerContainer}
        noScroll={assignment.assignment_type === 'reading'}// this assignment type has its own scroll view inside
        // @TODO: showsVerticalScrollIndicator={false}
      >
        {/* @TODO: should this be in a dialog instead of inline? i think this might be a better ux if it's a full-screen dialog or even a navigated-to view (yes, and when dialog is open, have timer going that tracks how longer a module is being worked on even if opening/closing multiple times; also, will require grades table to include in-progres metadata like time spent, etc.) */}
        <View style={styles.assignmentWrapper}>
          {assignment.assignment_type === 'lecture' &&
            <Assignments.Lecture
              hideActions={hideActions}
              {...assignment.assignment_object}
              progress={progress}
              goalId={goal.id}
            />
          }
          {assignment.assignment_type === 'verse_memory' &&
            <Assignments.VerseMemory
              hideActions={hideActions}
              {...assignment.assignment_object}
              passage={passage}
              progress={progress}
              goalId={goal.id}
              assignmentId={assignment.id}
              showInstructions={true}
            />
          }{/* @TODO: would prefer not to have to pass goalId */}
          {assignment.assignment_type === 'reading' &&
            <Assignments.Reading
              hideActions={hideActions}
              {...assignment.assignment_object}
              passage={passage}
              progress={progress}
              goalId={goal.id}
              finishButtonJsx={finishButtonJsx}
            />
          }
        </View>

        {(!hideActions && !['verse_memory', 'reading'].includes(assignment.assignment_type)) && finishButtonJsx}
      </FullScreenScrollView>
    </ScreenWrapper>
    // @TODO: need bottom padding because of iphone
  );
};

export default Assignment;
